/** Text element structure */
export type TextElement = {
    /** Change type (ADDED, DELETED, null) */
    changeType:
        | 'ADDED'
        | 'MODIFIED'
        | 'DELETED'
        | 'ACCEPTED'
        | 'REPLACED'
        | undefined
        | null
    /** Element ID (data-id attribute) */
    id: string
    /** Id of the author of the change */
    authoredUserId: string
    /** parsed Text content */
    text: string
}

/** parsed suggestion from "name" attribute */
type Context = {
    change: TextElement['changeType']
    /** Text element uuid */
    textId: string
    /** Author identifier from suggestion marker */
    userId: string
}

/**
 * Converts HTML (span.paragraph-text) with tracked changes to/from TextElement structures.
 * Parse suggestion markers: <suggestion-start> and <suggestion-end>
 * @example
 * const parser = new TextElementParser();
 * const elements = parser.parse('<suggestion-start...>...');
 */
export class TextElementParser {
    private readonly parser = new DOMParser()
    /** Base context for text element without suggestions */
    private readonly baseContext: Context = {
        change: null,
        userId: '',
        textId: '',
    }

    /**
     * Parse HTML into structured TextElements
     * @param html Input HTML with <suggestion-start/end> tags
     * @returns Array of parsed text elements
     * @example parser.process('<suggestion-start...>...') // returns TextElement[]
     */
    process(html: string): TextElement[] {
        try {
            const span = this.parseRootSpan(html)
            const paragraph = this.parseRootParagraph(html)
            return span
                ? this.processSpan(span)
                : paragraph
                ? this.processParagraph(paragraph)
                : []
        } catch (error) {
            console.error('Parsing failed:', error)
            return []
        }
    }

    /** Find root span.paragraph-text element */
    private parseRootSpan(html: string): HTMLSpanElement | null {
        return this.parser
            .parseFromString(html, 'text/html')
            .querySelector('span.paragraph-text')
    }

    private parseRootParagraph(html: string): HTMLParagraphElement | null {
        return this.parser.parseFromString(html, 'text/html').querySelector('p')
    }

    /** Main processing pipeline for span content */
    private processSpan(span: HTMLSpanElement): TextElement[] {
        const elements: TextElement[] = []
        const contextStack = this.initContextStack(span)

        this.traverse(span.childNodes, elements, {
            elementId: span.getAttribute('data-id') || '', // span's data-id as parent text id
            contextStack,
        })

        return elements
    }

    private processParagraph(paragraph: HTMLParagraphElement): TextElement[] {
        const elements: TextElement[] = []
        const contextStack = this.initContextStack(paragraph)

        this.traverse(paragraph.childNodes, elements, {
            elementId: paragraph.getAttribute('data-id') || '', // paragraph's data-id as parent text id
            contextStack,
        })

        return elements
    }

    /** Initializes context stack with inherited values */
    private initContextStack(
        element: HTMLSpanElement | HTMLParagraphElement
    ): Context[] {
        const stack = [this.baseContext]
        const initialContext = this.getInitialContext(element)

        if (initialContext.change || initialContext.userId) {
            stack.push(initialContext)
        }
        return stack
    }

    /** Recursive node processor - handles text/element types */
    private traverse(
        nodes: NodeList,
        elements: TextElement[],
        {
            elementId,
            contextStack,
        }: { elementId: string; contextStack: Context[] }
    ) {
        nodes.forEach((node) => {
            if (node.nodeType === Node.TEXT_NODE) {
                this.processText(node, elements, elementId, contextStack)
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                this.processElement(
                    node as HTMLElement,
                    elements,
                    elementId,
                    contextStack
                )
            }
        })
    }

    /**
     * Handle text node - push element if non-empty
     * TODO: check if API requires authoredUserId,
     * for some reason it work for "ADDED" changeType and breaks the highlight changes feature on CKE.
     * */
    private processText(
        node: Node,
        elements: TextElement[],
        elementId: string,
        contextStack: Context[]
    ) {
        const text = node.textContent
        if (!text) return

        const currentContext = contextStack[contextStack.length - 1]
        elements.push({
            id: elementId,
            text,
            authoredUserId: '',
            changeType: currentContext.change,
        })
    }

    /**
     * Handle extraction of context from suggestion markers (<suggestion-start> and <suggestion-end>)
     * @param el - Element to process
     * @param elements - Array to store processed elements
     * @param elementId - ID of the current text element
     * @param { Context[] } contextStack - Current state of processing
     * */
    private processElement(
        el: HTMLElement,
        elements: TextElement[],
        elementId: string,
        contextStack: Context[]
    ) {
        const tag = el.tagName.toLowerCase()

        if (tag === 'suggestion-start') {
            contextStack.push(this.parseContext(el))
            this.traverse(el.childNodes, elements, { elementId, contextStack })
        } else if (tag === 'suggestion-end' && contextStack.length > 1) {
            contextStack.pop()
        } else {
            this.traverse(el.childNodes, elements, { elementId, contextStack })
        }
    }

    /**
     * Extract context from suggestion-start's name attribute
     * which should have the format: [type:textId:userId]
     * @param el - suggestion-start element
     * @returns Context object
     * */
    private parseContext(el: HTMLElement): Context {
        const [type, textId, userId] = (el.getAttribute('name') || '').split(
            ':'
        )
        return {
            change:
                type === 'deletion'
                    ? 'DELETED'
                    : type === 'insertion'
                    ? 'ADDED'
                    : null,
            textId: textId || '',
            userId: userId || '',
        }
    }

    /** Gets context from previous sibling suggestion-start */
    private getInitialContext(span: Node): Context {
        let node = span.previousSibling

        while (node) {
            if (node.nodeType === Node.ELEMENT_NODE) {
                const el = node as HTMLElement
                // Checks previous siblings for inherited context
                if (el.tagName.toLowerCase() === 'suggestion-start') {
                    return this.parseContext(el)
                }
            }
            // move to next
            node = node.previousSibling
        }

        // otherwise, return base context
        return this.baseContext
    }
}
